import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { userRequest } from '../utils/requestMethods';
import { Header, BackButton, SkeletonCards, ConfirmationModal } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import * as Icons from '../data/class';
import { FiDownloadCloud } from 'react-icons/fi';
import AddLessonNoteModal from '../components/teacherModule/AddLessonNoteModal';

const LessonNotes = ({ history }) => {
  const location = useLocation();
  const subjectId = location.pathname.split('/')[2];
  const classId = location.pathname.split('/')[3];
  const subject = location.state?.subject;
  const { school, user } = useStateContext();
  const schoolId = school?._id;
  const schoolTerm = school?.settings?.current_term;
  const schoolSession = school?.settings?.current_session;
  const [subjects, setSubjects] = useState([]);
  const [classInfo, setClassInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchSubjects();
    fetchClass();
  }, [classId, schoolId]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const { data } = await userRequest.get(`teachers/lesson-notes/${schoolId}/${classId}/${subjectId}?term=${schoolTerm}&session=${schoolSession}`);
      //console.log(data.lesson_notes);
      setSubjects(data.lesson_notes);
      setLoading(false);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClass = async () => {
    try {
      const { data } = await userRequest.get(`classes/${schoolId}/${classId}`);
      setClassInfo(data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDownload = (attachmentUrl, title) => {
    const link = document.createElement('a');
    link.href = attachmentUrl;
    link.download = title;
    link.click();
  };

  const handleNoteUploaded = () => {
    fetchSubjects(); // Refresh the lesson notes list
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;
    
    try {
      setDeleting(true);
      await userRequest.delete(`teachers/lesson-notes/${schoolId}/${classId}/${subjectId}/${noteToDelete._id}`);
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setNoteToDelete(null);
      
      // Refresh the lesson notes list
      fetchSubjects();
    } catch (error) {
      console.error('Error deleting lesson note:', error);
      // You might want to show an error notification here
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (note) => {
    setNoteToDelete(note);
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setNoteToDelete(null);
  };

  return (
    <>
      <div className="px-4 md:px-10 pt-6">
        <BackButton history={history} />
      </div>
      <div className="px-4 md:px-10">
        <div className="flex flex-wrap justify-between items-center">
          <Header category="Lesson Notes" title={subject?.subject_name || "Loading..."} />
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-[#3F3DA5] text-white px-6 py-2 rounded-full hover:bg-[#5552db] transition-colors font-medium"
          >
            Upload
          </button>
        </div>
      </div>

      <div className="mx-2 md:mx-10 p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {loading ? (
            <SkeletonCards cardCount={3} layout="vertical" width="" />
          ) : subjects.length > 0 ? (
            subjects.map((subject, index) => (
              <div key={index} className='pr-0 sm:pr-3'>
                <div className='border border-[#C5C5E8] rounded-3xl'>
                  <div className='bg-[#EEEEFC] flex items-center rounded-tr-3xl rounded-tl-3xl justify-center px-4 py-8 md:py-14'>
                    <img src={Icons.fileType} alt="Nav Icon" className='w-16 h-16 md:w-20 md:h-20' />
                  </div>
                  <div className='flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between p-3'>
                    <div className='flex-1 min-w-0 mb-3 sm:mb-0'>
                      <p className='text-sm font-semibold mt-3 truncate' title={subject.title}>{subject.title}</p>
                      <p className='text-xs mt-2 mb-3'>Uploaded {(new Date(subject.updated_at)).toLocaleDateString('en-NG')}</p>
                    </div>
                    <div className="flex items-center justify-end sm:justify-start">
                      <button
                        onClick={() => handleDownload(subject.attachment, subject.title)}
                        className="p-2 text-blue-500 rounded-full bg-blue-100 hover:text-blue-700 hover:bg-blue-200"
                      >
                        <FiDownloadCloud className="text-xl md:text-2xl" />
                      </button>
                      <button 
                        onClick={() => confirmDelete(subject)}
                        className="ml-4 p-2 text-red-500 rounded-full bg-red-100 hover:text-red-700 hover:bg-red-200"
                      >
                        <img src={Icons.delete} alt="Delete Icon" className="w-5 h-5 md:w-6 md:h-6" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full flex items-center justify-center min-h-[400px]">
              <div className="text-center text-gray-500">
                <p>No lesson note uploaded for the subject yet.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <AddLessonNoteModal
          closeModal={() => setShowUploadModal(false)}
          onNoteUploaded={handleNoteUploaded}
          schoolId={schoolId}
          classId={classId}
          //teacherId={user?._id} // You may need to adjust this based on your user context
          subjectId={subjectId}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isVisible={showDeleteModal}
        title="Delete Lesson Note"
        description={`Are you sure you want to delete "${noteToDelete?.title}"? This action cannot be undone.`}
        onConfirm={handleDeleteNote}
        onCancel={cancelDelete}
        deleting={deleting}
      />
    </>
  );
};

export default LessonNotes;
